import React, { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Protection } from "@sportal/api";
import _ from "lodash";

import { useIntlTranslate } from "../../../../../hooks";
import { getProfileProtection } from "../../../../../store/profiles/profiles.selectors";
import { getNames } from "../../../../../store/categories/categories.helpers";
import {
  getAllowed,
  getBlocked,
  getDetailedCategoriesWithActions,
} from "../webFilters.helpers";
import { useFilterCategories } from "../hooks";
import { ProtectionStatus } from "./protectionEditor.constants";
import { CategoriesList } from "./CategoriesList";
import { ProtectionEditorStatusFilters } from "./ProtectionEditorStatusFilters";
import { ProtectionEditorActions } from "./ProtectionEditorActions";
import { ProtectionCategoriesStatus } from "./ProtectionCategoriesStatus";
import { ProtectionSearch } from "../protectionSearch/ProtectionSearch";
import { CategoriesByStatus } from "../webFilters.types";
import { ProtectionEditorCategoriesSummary } from "./ProtectionEditorCategoriesSummary";
import { ChangeProtectionCategoriesFn } from "./protectionEditor.types";

import "./ProtectionEditor.scss";

const defaultStatusFilters = [
  ProtectionStatus.Allowed,
  ProtectionStatus.Blocked,
];

interface UseActionWithFilterResetProps {
  setNameFilter: (name: string) => void;
  setStatusFilter: (statusFilter: ProtectionStatus[]) => void;
  protection: Protection;
}

const useActionWithFilterReset = ({
  setNameFilter,
  setStatusFilter,
  protection,
}: UseActionWithFilterResetProps) => {
  const shouldResetFilters = useRef(true);

  useEffect(() => {
    if (shouldResetFilters.current) {
      setNameFilter("");
      setStatusFilter(defaultStatusFilters);
    }
  }, [protection, setNameFilter, setStatusFilter]);

  useEffect(() => {
    shouldResetFilters.current = true;
  });

  return (action: ChangeProtectionCategoriesFn, categoryNames: string[]) => {
    shouldResetFilters.current = false;
    action(categoryNames);
  };
};

interface ProtectionEditorProps {
  header: ReactNode;
  profileId: string;
  categories: CategoriesByStatus;
  onBlock: ChangeProtectionCategoriesFn;
  onAllow: ChangeProtectionCategoriesFn;
  blockedByWebFilter?: string[];
  disabled?: boolean;
}

export const ProtectionEditor = ({
  header,
  profileId,
  categories,
  onBlock,
  onAllow,
  blockedByWebFilter,
  disabled,
}: ProtectionEditorProps) => {
  const translate = useIntlTranslate();

  const [statusFilter, setStatusFilter] =
    useState<ProtectionStatus[]>(defaultStatusFilters);

  const protection: Protection = useSelector(getProfileProtection(profileId));

  const detailedCategories = getDetailedCategoriesWithActions(
    categories,
    onBlock,
    onAllow,
    blockedByWebFilter,
    protection,
    translate
  );

  const { input, setInput, filteredCategories } = useFilterCategories(
    detailedCategories,
    statusFilter
  );

  const allowedCategories = filteredCategories.filter(getAllowed);
  const blockedCategories = filteredCategories.filter(getBlocked);

  const handleFilterStatusChange = (status: ProtectionStatus): void => {
    setStatusFilter((prevState) =>
      _.includes(prevState, status)
        ? _.without(prevState, status)
        : _.concat(prevState, status)
    );
  };

  const handleCategoryChange = useActionWithFilterReset({
    setNameFilter: setInput,
    setStatusFilter,
    protection,
  });

  return (
    <Fragment>
      {header}
      <div className="protection-editor" data-testid={"protection-editor"}>
        <div className={"protection-editor__header"}>
          <ProtectionSearch
            inputId={"categories-search"}
            onChange={setInput}
            disabled={disabled}
            filterValue={input}
          />
          <ProtectionEditorStatusFilters
            filterStatus={statusFilter}
            onChange={handleFilterStatusChange}
            disabled={disabled}
          />
        </div>
        <ProtectionCategoriesStatus
          allCategoriesCount={
            categories.allowed.length + categories.blocked.length
          }
          filteredCategoriesCount={filteredCategories.length}
        />
        <div className={"protection-editor__list-container"}>
          <CategoriesList
            list={filteredCategories}
            onClick={handleCategoryChange}
            disabled={disabled}
          />
        </div>
        <div className={"protection-editor__footer"}>
          <div className="categories-summary">
            <ProtectionEditorCategoriesSummary
              categoriesCounter={blockedCategories.length}
              messageId="blocked"
            />
            <ProtectionEditorCategoriesSummary
              categoriesCounter={allowedCategories.length}
              messageId="allowed"
            />
          </div>
          <ProtectionEditorActions
            onAllowAllClick={() =>
              handleCategoryChange(onAllow, blockedCategories.map(getNames))
            }
            onBlockAllClick={() =>
              handleCategoryChange(onBlock, allowedCategories.map(getNames))
            }
            blockDisabled={_.isEmpty(allowedCategories) || disabled}
            allowDisabled={_.isEmpty(blockedCategories) || disabled}
          />
        </div>
      </div>
    </Fragment>
  );
};
